import React from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, Chip, Dialog, DialogContent, Divider, IconButton, Stack, Typography, CircularProgress } from '@mui/material';
import { Close, DownloadDone, Favorite, ChatBubbleOutline } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  PLAY_ATHLETE,
  FOLLOW_USER,
  UNFOLLOW_USER,
  SUGGESTED_ATHLETES,
  MY_FOLLOWERS_COUNT
} from 'api/graphql/play';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { muscleLabel } from 'components/play/constants';

const initials = (name) => (name || '?').trim().charAt(0).toUpperCase();
const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};

/**
 * OTRIX Play — athlete profile: photo, time on OTRIX, follower/following/routine counts, a
 * follow toggle and the list of their published routines. Opened from the forum / comments.
 */
function PlayAthleteModal({ userId = null, open, onClose }) {
  const { t } = useTranslation(['play']);

  const { data, loading } = useQuery(PLAY_ATHLETE, {
    variables: { user_id: userId },
    skip: !userId || !open,
    fetchPolicy: 'cache-and-network'
  });
  const athlete = data?.playAthlete;

  const refetch = [
    { query: PLAY_ATHLETE, variables: { user_id: userId } },
    { query: SUGGESTED_ATHLETES },
    { query: MY_FOLLOWERS_COUNT }
  ];
  const [follow] = useMutation(FOLLOW_USER, { refetchQueries: refetch });
  const [unfollow] = useMutation(UNFOLLOW_USER, { refetchQueries: refetch });

  const toggleFollow = () => {
    if (!athlete) return;
    const mutation = athlete.is_followed ? unfollow : follow;
    mutation({ variables: { user_id: athlete.id } });
  };

  const stat = (value, label) => (
    <Stack alignItems="center" sx={{ flex: 1 }}>
      <Typography variant="subtitle1" fontWeight={800}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Typography variant="subtitle1" fontWeight={800}>
          {t('athlete.title', { ns: 'play', defaultValue: 'Atleta' })}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent sx={{ p: 2.5 }}>
        {loading && !athlete ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress color="secondary" />
          </Stack>
        ) : (
          athlete && (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar src={athlete.photo || undefined} sx={{ width: 64, height: 64, bgcolor: 'gold.soft', color: 'gold.main', fontSize: '1.4rem' }}>
                  {initials(athlete.full_name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={800} noWrap>
                    {athlete.full_name}
                  </Typography>
                  {athlete.member_since && (
                    <Typography variant="caption" color="text.secondary">
                      {t('forum.memberSince', { ns: 'play', date: fmtDate(athlete.member_since), defaultValue: `En OTRIX desde ${fmtDate(athlete.member_since)}` })}
                    </Typography>
                  )}
                </Box>
                {!athlete.is_me && (
                  <Button
                    onClick={toggleFollow}
                    variant={athlete.is_followed ? 'outlined' : 'contained'}
                    color="inherit"
                    sx={athlete.is_followed ? { textTransform: 'none', fontWeight: 700, borderColor: 'hairline.strong', color: 'text.primary' } : { ...premiumButtonSx }}>
                    {athlete.is_followed ? t('forum.unfollow', { ns: 'play', defaultValue: 'Siguiendo' }) : t('forum.follow', { ns: 'play', defaultValue: 'Seguir' })}
                  </Button>
                )}
              </Stack>

              <Stack direction="row" sx={{ mt: 2.5, mb: 1 }} divider={<Divider orientation="vertical" flexItem />}>
                {stat(athlete.followers_count, t('forum.followers', { ns: 'play', defaultValue: 'Seguidores' }))}
                {stat(athlete.following_count, t('forum.followingLabel', { ns: 'play', defaultValue: 'Siguiendo' }))}
                {stat(athlete.routines_count, t('forum.routinesLabel', { ns: 'play', defaultValue: 'Rutinas' }))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {(athlete.routines || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {t('athlete.noRoutines', { ns: 'play', defaultValue: 'Aún no ha publicado rutinas.' })}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {athlete.routines.map((r) => (
                    <Box key={r.id} sx={{ p: 1.5, borderRadius: '14px', border: '1px solid', borderColor: 'hairline.main', bgcolor: 'surface.raised' }}>
                      <Typography variant="body2" fontWeight={800} noWrap>
                        {r.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                        {r.muscle_focus && <Chip size="small" label={muscleLabel(t, r.muscle_focus)} sx={{ bgcolor: 'gold.soft', color: 'gold.main', height: 20 }} />}
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <DownloadDone sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{r.adopt_count || 0}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <Favorite sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{r.like_count || 0}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <ChatBubbleOutline sx={{ fontSize: 14 }} />
                          <Typography variant="caption">{r.comment_count || 0}</Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

PlayAthleteModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PlayAthleteModal;
